import * as i18nextko from 'i18next-ko';
import type { Answer, AttachmentForm, ContactRequestBody, Language } from '../../../../crm/crmTypes';
import { CrmApiClient } from '../../../../crm/crmApiClient';
import { FormType } from '../../formUtils';
import { BundledSettingsProvider } from '../../../../configuration/bundledSettingsProvider';
import { onShowTogglable } from '../../../../modules/core/togglables';

const maxFileSize = 104857600;
const supportedContentTypes = [
    'application/pdf', 
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];

declare const grecaptcha: ReCaptchaInstance;
  
interface ReCaptchaInstance {
    ready: (cb: () => any) => void;
    execute: (siteKey: string, options: ReCaptchaExecuteOptions) => Promise<string>;
}
interface ReCaptchaExecuteOptions {
    action: string;
}

function onReCaptchaReady(): Promise<void> {
    return new Promise<void>((resolve) => {
        if (window['grecaptcha']) {
            grecaptcha.ready(resolve);
        } else {
            resolve();
        }
    });
}

export class FormHTMLElement extends HTMLElement {
    private dropdownsObserver: MutationObserver;
    private readonly crmApiClient: CrmApiClient;
    private readonly reCaptchaSiteKey: string;
    
    constructor() {
        super();
        const settingsProvider = BundledSettingsProvider.newInstance();
        const endpoint: string = settingsProvider.getSettingSync<string>('crm/publicEndpoint');
        this.reCaptchaSiteKey = settingsProvider.getSettingSync<string>('reCaptcha/siteKey');
        this.crmApiClient = CrmApiClient.build(endpoint);
    }

    private formSubmitHandler = async (e: Event): Promise<void> => {
        const target = e.target as HTMLElement;
        const form = target.closest('form') as HTMLFormElement;
        form.classList.remove('generic-error');

        const button = form.querySelector<HTMLButtonElement>('button');
        button.classList.add('loading');
        button.disabled = true;

        await onReCaptchaReady();

        try {
            const token: string = window['grecaptcha'] ? await grecaptcha.execute(this.reCaptchaSiteKey, {action: 'submit'}) : null;

            const type = this.getAttribute('form-type');

            const params: ContactRequestBody & { challenge: string, referrer?: string } = {
                sourcePageURL: window.top.location.href,
                firstName: '',
                lastName: '',
                email: '',
                replySiteCompany: '',
                replySiteLanguage: this.getAttribute('language') as keyof typeof Language,
                doNotBulkEmail: type !== 'supplier',
                selectedAnswers: [] as Answer[],
                consents: [],
                challenge: token,
                referrer: document.referrer
            };

            for (const { id, checked, name, value, dataset } of Array.from(form.elements) as (HTMLInputElement & HTMLTextAreaElement)[]) {
                if (name === 'confirmBtn' || !value) {
                    // skip
                } else if (name === 'doNotBulkEmail') {
                    if (checked) {
                        params[name] = value === 'true';
                    }
                } else if (id?.startsWith('q_')) { // additional questions
                    if (checked) {
                        const question = params.selectedAnswers.find(el => el.questionId === name);
                        if (question) {
                            question.answers.push(dataset.optionId);
                        } else {
                            params.selectedAnswers.push({
                                questionId: name,
                                answers: [ dataset.optionId ]
                            });
                        }
                    }
                } else if (id?.startsWith('file_')) {
                    if (dataset.key) { // send only when there are files
                        (params.attachments = params.attachments || []).push({
                            key: dataset.key.replace('u/', '')
                        });
                    }
                } else if (name.startsWith('p_')) {
                    if (checked) {
                        const n = name.replace(/^p_/, '');
                        (params[n] = params[n] || []).push(dataset.optionId);
                    }
                } else if (name.startsWith('pn_')) {
                    if (checked) {
                        params.consents.push({
                            partner: name.replace('pn_', ''),
                            consent: value === 'true'
                        });
                    }
                } else {
                    params[name] = value;
                }
            }

            const gaEvent: any = {
                event: 'form_submit',
                eventCategory: `form_${type}`,
                userCompany: params.replySiteCompany,
                userRole: params.jobTitle || '',
                privacy_consent: params.doNotBulkEmail ? 'NO' : 'YES',
                title: this.getAttribute('form-title') || 'null'
            };

            window.dataLayer.push({
                ...gaEvent,
                eventAction: 'form_submit',
            });

            try {
                await this.crmApiClient.requestContact(this.getAttribute('resource-id'), params);
            } catch (e) {
                form.classList.add('generic-error');
                console.error(`[Form ${this.id}] Submit went wrong: `, e);
                return;
            }

            target.removeEventListener('click', this.formSubmitHandler);

            const modal = target.closest('.modal');
            modal.classList.replace('modal--form', 'modal--completed');
            modal.querySelector('.modal__external-dismiss').classList.add('!hidden');
            modal.querySelector('.modal__content').classList.add('hidden');
            modal.querySelector('.form__completed').classList.remove('hidden');

            window.dataLayer.push({
                ...gaEvent,
                eventAction: 'TYP',
            });

        } catch (e) {
            form.classList.add('generic-error');
            console.error(`[Form ${this.id}] reCAPTCHA execution went wrong: `, e);

        } finally {
            button.classList.remove('loading');
            button.disabled = false;
        }
    }

    private formValidation(): void {
        const form = this.closest('form') || this.querySelector('form') as HTMLFormElement;
        form.classList.remove('generic-error');

        let formValidity = true;

        for (const el of Array.from(form.elements) as (HTMLInputElement & HTMLTextAreaElement)[]) {
            const { files, id, required, type, value } = el;
            if (type === 'submit') {
                continue;
            }
            const formInput: HTMLElement = el.closest('[class^="form__"]');
            const formInputClasses = formInput.classList;
            let valid = true;
            switch (type) {
                case 'email':
                case 'tel':
                case 'text':
                case 'textarea':
                    formInputClasses.toggle('filled', value.length > 0);
                    valid = el.checkValidity();
                    formInputClasses.toggle('valid', valid);
                    break;
                case 'checkbox':
                    if (formInputClasses.contains('form__checkbox')) {
                        valid = el.checkValidity();
                    } else {
                        const isSomethingChecked = formInput.querySelector('button > span').textContent !== formInput.title;
                        formInputClasses.toggle('filled', isSomethingChecked);
                        if (required) {
                            valid = isSomethingChecked;
                        }
                    }
                    break;
                case 'radio': {
                    if (id.startsWith('q_')) {
                        const isSomethingChecked = formInput.querySelector('button > span').textContent !== formInput.title;
                        formInputClasses.toggle('filled', isSomethingChecked);
                        if (required) {
                            valid = isSomethingChecked;
                        }
                    } else {
                        valid = el.checkValidity();
                    }
                    break;
                }
                case 'file': {
                    formInputClasses.toggle('filled', Boolean(files[0]));

                    let thisFileIsValid: boolean = false;
                    let errorMsg: string;
                    if (files[0] && !formInputClasses.contains('loading')) {
                        const {name, size, type} = files[0];
                        el.nextElementSibling.querySelector('span').textContent = name;
                        if (size > maxFileSize) {
                            errorMsg = 'tooBig';
                        }
                        if (!supportedContentTypes.includes(type)) {
                            errorMsg = 'unsupported';
                        }
                        const error = errorMsg || formInputClasses.contains('generic-error');
                        el.nextElementSibling.querySelector('small').textContent = error
                            ? i18nextko.i18n.t([`form_fileError.${errorMsg}`, 'form_fileError.unspecific'])
                            : '';
                        formInputClasses.toggle('valid', !error);
                        thisFileIsValid = !error;
                    } else if (!files[0]) {
                        el.removeAttribute('data-key');
                        el.nextElementSibling.querySelector('span').textContent = i18nextko.i18n.t('form_fileNoAttachment');
                        el.nextElementSibling.querySelector('small').textContent = '';
                        formInputClasses.remove('valid');
                    }

                    if (!formInput.nextElementSibling) { // truly check files validity only at the last one
                        const someFileIsValid = Array.from(formInput.parentElement.children).some(child =>
                            child.classList.contains('form__file') && child !== formInput && child.classList.contains('valid')
                        );
                        valid = thisFileIsValid || someFileIsValid;
                    } else {
                        valid = true;
                    }
                    break;
            }
            default:
                    valid = el.checkValidity();
                    break;
            }
            if (!valid) {
                formValidity = false;
            }
        }

        (<HTMLButtonElement>form.querySelector('button[name="confirmBtn"]')).disabled = !formValidity;
    }

    private attachmentSubmit = async (e: Event): Promise<void> => {
        const crmApiClient = this.crmApiClient;
        const target = <HTMLInputElement>e.target;
        const targetFile = target.files[0];
        if (targetFile) {
            const { name, size, type } = targetFile;
            const formInputClasses = target.parentElement.classList;
            const tooBig = size > maxFileSize;
            const unsupported = !supportedContentTypes.includes(type);
            if (!unsupported && !tooBig) {
                formInputClasses.add('loading');
                await onReCaptchaReady();

                try {
                    const token: string = window['grecaptcha'] ? await grecaptcha.execute(this.reCaptchaSiteKey, {action: 'submit'}) : null;

                    try {
                        const { url, fields }: AttachmentForm = await crmApiClient.requestAttachment({
                            challenge: token,
                            contentType: type
                        });
                        target.setAttribute('data-key', fields.key);
                        await crmApiClient.submitAttachment({ url, fields, file: targetFile });
                        formInputClasses.remove('generic-error');
                    } catch (e) {
                        console.error(`[Form file "${name}"] Attachment submit went wrong: `, e);
                        formInputClasses.add('generic-error');
                    }

                } catch (e) {
                    console.error(`[Form file "${name}"] reCAPCTHA execution went wrong: `, e);
                    formInputClasses.add('generic-error');

                } finally {
                    formInputClasses.remove('loading');
                    this.formValidation.call(target);
                }
            }
        }
    }

    private redirectRoseGA = (e: Event): void => {
        const target = <HTMLAnchorElement>e.target;
        window.dataLayer= window.dataLayer|| [];
        window.dataLayer.push({
            event: 'rose_credentials_form',
            eventCategory: `cta - ${this.getAttribute('product-code-Rose')}`,
            eventAction: 'redirect to rose',
            eventLabel: `language_${this.getAttribute('language')}`,
            title:  this.getAttribute('form-title') || 'null',
            page_location: window.top.location.href,
            destination_url: target.href
        });
    }
    private selectApply = (e: Event): void => {
        const target = <HTMLButtonElement>e.target;
        const select: HTMLElement = target.closest('.form__select');
        const button = select.querySelector('button');
        const dropdown: HTMLElement = select.querySelector('.select__dropdown');
        const list = Array.from(dropdown.querySelector('ul').children);
        const [values, ids] = list.reduce((acc, el) => {
            const input = el.querySelector('input')
            if (input.checked) {
                return [[...acc[0], input.value], [...acc[1], input.id]]
            } else {
                return acc
            }
        }, [[], []])
        const selectSpan = select.querySelector<HTMLElement>('button > span')
        selectSpan.textContent = values.length ? values.join(', ') : select.title;
        selectSpan.dataset.selected = JSON.stringify(ids);
        onShowTogglable(button, dropdown);
        this.formValidation.call(select);
    }

    private scrollIntoModalView(e: Event) {
        const target = e.target as HTMLElement;
        if (target?.scrollIntoView) {
            target.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    }

    private modalToggleHandler = (e: CustomEvent): void => {
        const { modalOpen, target } = e.detail;
        
        if (modalOpen) {

            for (const el of target.querySelectorAll('.select__dropdown')) {
                this.dropdownsObserver.observe(el, {
                    attributes: true,
                    attributeFilter: ['class']
                });
            }

            for (const fs of target.querySelectorAll('.select__dropdown > button') || []) {
                fs.addEventListener('click', this.selectApply);
            }

            target.addEventListener('input', this.formValidation.bind(target));
            
            for (const el of target.querySelectorAll('input')) {
                el.addEventListener('focus', this.scrollIntoModalView.bind(this));
            }

            for (const fs of target.querySelectorAll('.form__file input') || []) {
                fs.addEventListener('input', this.attachmentSubmit);
            }

            target.querySelector('button[name="confirmBtn"]')?.addEventListener('click', this.formSubmitHandler);

            target.querySelector("#hlCodeROSE")?.addEventListener('click', this.redirectRoseGA);

            const type = this.getAttribute('form-type');
            const gaEvent: any = {
                event: 'form_submit',
                eventCategory: `form_${type}`,
                eventLabel: `language_${this.getAttribute('language')}`
            };

            switch (type) {
                case FormType.Download:
                    gaEvent.eventAction = 'click_first_step_download';
                    gaEvent.title = this.getAttribute('form-title');
                    break;
                default:
                    gaEvent.eventAction = `click_first_step_${this.getAttribute('button-label')}`;
                    break;
            }
            
            window.dataLayer.push(gaEvent);

        } else {
            const type = this.getAttribute('form-type');
            const gaEvent: any = {
                event: 'form_submit',
                eventCategory: `form_${type}`,
                eventAction: 'click_close_overlay',
                eventLabel: `language_${this.getAttribute('language')}`
            };

            switch (type) {
                case FormType.Download:
                    gaEvent.title = this.getAttribute('form-title');
                    break;
            }
            
            window.dataLayer.push(gaEvent);
        }
    }

    public async connectedCallback(): Promise<void> {
        window.dataLayer = window.dataLayer || [];

        if (this.reCaptchaSiteKey && !document.head.querySelector('script[src*=recaptcha]')) {
            const headScriptElement = document.createElement('script');
            headScriptElement.src = `https://www.google.com/recaptcha/api.js?render=${this.reCaptchaSiteKey}`;
            headScriptElement.async = true;
            document.head.insertAdjacentElement('beforeend', headScriptElement);
        }

        this.dropdownsObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                const target = <HTMLElement>mutation.target;
                if (mutation.type === 'attributes' && !target.classList.contains('show')) {
                    const list = Array.from(target.querySelector('ul').children);
                    const strCheckedValues = target.closest('.form__select').querySelector<HTMLElement>('button > span').dataset.selected;
                    const checkedValues = strCheckedValues ? JSON.parse(strCheckedValues) : [];
                    for (const el of list) {
                        const input = el.querySelector('input');
                        input.checked = checkedValues.includes(input.id);
                    }
                }
            }
        });
        
        setTimeout(() => {
            const toggle = this.querySelector('[data-toggle]') as HTMLElement;

            toggle.addEventListener('modaltoggle', this.modalToggleHandler);

            const param = new URLSearchParams(window.parent.location.search).get('formId');
            
            if (param === this.id) {
                toggle.click();
            }
        });
    }

    public disconnectedCallback(): void {
        this.querySelector('[data-toggle]').removeEventListener('modaltoggle', this.modalToggleHandler);
    }
}